import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Formik, Form, Field } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { useTracking } from 'react-tracking';
import dynamic from 'next/dynamic';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { createStructuredSelector } from 'reselect';
import { withTranslation, Router } from '../../src/i18n';
import {
  selectArtistProfessionsOptions,
  selectProfilesListOptions,
  selectProfilesListLoading,
  selectVoiceTypesOptions,
} from '../../containers/App/selectors';
import route from '../../constants/routes';
import { clearProfilesAction, getProfilesAction } from '../../containers/App/actions';
import { ARTIST_TYPE_ID, TP, SINGER_PROFESSION_TYPE_ID } from '../../constants/consts';
import { getRouteWithSlug } from '../../utils/common';
import { trackSearch, trackSearchFilter, SEARCH_LABELS, trackSearchGo } from '../../utils/tracking';
import { colors } from '../../constants/styleConstants';

const AutocompleteSelect = dynamic(() => import('../Fields/AutocompleteSelect'));
const FormikSearchField = dynamic(() => import('../Fields/SearchFieldV2/FormikSearchField'));

const useStyles = makeStyles(theme => ({
  formGrid: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  field: {
    marginRight: '15px!important',
    '& input': {
      fontSize: '18px',
    },
  },
  button: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    minWidth: '68px',
    lineHeight: 1,
    margin: 0,
    padding: 0,
    boxShadow: '0 2px 4px 0 rgba(0, 31, 59, 0.25)',
    borderRadius: '2px',
    backgroundColor: colors.blue3,
    marginLeft: '5px',
    '&:hover': {
      backgroundColor: colors.blue3,
      boxShadow: '0 5px 5px 0 rgba(0, 31, 59, 0.25)',
    },
    '& span': {
      lineHeight: 1,
    },
  },
  filtersFields: {
    width: '100%',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      '& > *': {
        width: '46% !important',
      },
    },
  },
  filtersSearchItem: {
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    justifyContent: 'space-evenly',
    [theme.breakpoints.down('sm')]: {
      marginTop: 10,
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  fullWidth: {
    width: '100%',
    maxWidth: '100%',
  },
}));

export const artistFormFields = {
  artistName: 'query',
  voiceType: 'profession_qualification_id',
  profession: 'profession_id',
};

export const artistsInitialValues = {
  [artistFormFields.artistName]: '',
  [artistFormFields.voiceType]: undefined,
  [artistFormFields.profession]: undefined,
};

const ArtistsSearchFilters = ({
  t,
  submitFunc,
  voiceTypes,
  professions,
  artists,
  variant,
  artistsLoading,
  initialValues,
  fullWidth,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const tracking = useTracking();
  const isMainArtistSearchFilter = true;

  const searchArtistsByName = debounce((query, values) => {
    const voiceTypeValue = get(values, `${artistFormFields.voiceType}.value`);
    const professionValue = get(values, `${artistFormFields.profession}.value`);
    dispatch(
      getProfilesAction({
        type: ARTIST_TYPE_ID,
        query,
        pagination: { limit: 10 },
        filters: {
          [artistFormFields.voiceType]: voiceTypeValue !== 0 ? voiceTypeValue : undefined,
          [artistFormFields.profession]: professionValue !== 0 ? professionValue : undefined,
        },
        from: 'search',
      }),
    );
  }, 200);

  const updatedArtistsInitialValues = {
    ...artistsInitialValues,
    ...{
      [artistFormFields.voiceType]: { value: 0, label: t('operabase.general.m_ALL'), type: '' },
      [artistFormFields.profession]: { value: 0, label: t('operabase.general.m_ALL'), type: '' },
    },
  };

  if (!initialValues) {
    initialValues = updatedArtistsInitialValues;
  }

  const voiceTypesOptions = [
    {
      value: 0,
      label: t('operabase.general.m_SEASON_ALL'),
      type: '',
    },
  ].concat(voiceTypes);

  const professionsOptions = [
    {
      value: 0,
      label: t('operabase.general.m_SEASON_ALL'),
      type: '',
    },
  ].concat(professions);

  const filterChange = (setField, fieldName, value) => {
    // if not an artist profession clear voice type field
    if (fieldName === get(artistFormFields, 'profession') && get(value, 'value') !== SINGER_PROFESSION_TYPE_ID) {
      setField(artistFormFields.voiceType, {
        value: 0,
        label: t('operabase.general.m_SEASON_ALL'),
        type: '',
      });
    }
    if (fieldName === artistFormFields.voiceType) {
      setField(artistFormFields.profession, {
        value: 0,
        label: t('operabase.general.m_SEASON_ALL'),
        type: '',
      });
    }

    let query = { [fieldName]: value.value };
    if (value.value === 0) {
      query = {};
    }

    Router.push({
      pathname: route.ARTISTS,
      query,
    });

    tracking.trackEvent(trackSearchFilter(fieldName, value.value));
  };

  const submitFunction = values => {
    submitFunc(values);
    const obj = {};
    obj[SEARCH_LABELS.ARTIST] = values.query;
    tracking.trackEvent(trackSearchGo(obj));
  };

  const trackSearchValues = debounce(value => {
    tracking.trackEvent(trackSearch(SEARCH_LABELS.ARTIST, value));
  }, 500);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={values => {
        submitFunction(values);
      }}
    >
      {({ setFieldValue, values }) => {
        return (
          <Form className={classes.formGrid} autoComplete="off">
            {variant === 'filter' && (
              <div className={classes.filtersSearchItem}>
                <>
                  <div className={classes.filtersFields}>
                    <Field
                      customIndicator
                      isEditable={false}
                      className={classes.field}
                      key={`${artistFormFields.voiceType}-${values[artistFormFields.voiceType]}`}
                      instanceId={`voice-${artistFormFields.voiceType}`}
                      name={artistFormFields.voiceType}
                      label={<>{t(`${TP}.m_VOICE`)}</>}
                      component={AutocompleteSelect}
                      options={voiceTypesOptions}
                      onChange={v => v && filterChange(setFieldValue, artistFormFields.voiceType, v, values)}
                    />
                    <Field
                      customIndicator
                      isEditable={false}
                      className={classes.field}
                      key={`${artistFormFields.profession}-${values[artistFormFields.profession]}`}
                      instanceId={`profession-${artistFormFields.profession}`}
                      name={artistFormFields.profession}
                      label={t(`${TP}.m_EDIT_ROSTER_LIST_PROFESSION`)}
                      component={AutocompleteSelect}
                      options={professionsOptions}
                      onChange={v => v && filterChange(setFieldValue, artistFormFields.profession, v, values)}
                    />
                  </div>
                </>
              </div>
            )}
            <div className={classnames(classes.filtersSearchItem, { [classes.fullWidth]: fullWidth })}>
              <Field
                name={artistFormFields.artistName}
                id={artistFormFields.artistName}
                label={t(`${TP}.m_SEARCHFORARTIST`)}
                options={artists}
                onEnter={() => submitFunction(values)}
                onInputTextChange={v => {
                  trackSearchValues(v);
                  if (v) {
                    return searchArtistsByName(v, values);
                  }
                  dispatch(clearProfilesAction());
                }}
                onFocus={() => {
                  const { query } = values;
                  if (query && query.length >= 1) {
                    return searchArtistsByName(query, values);
                  }
                }}
                isMainArtistSearchFilter={isMainArtistSearchFilter}
                isLoading={artistsLoading}
                component={FormikSearchField}
                onChange={v => {
                  trackSearchValues(v.label);
                  Router.push(`${route.ARTISTS}/[id]/[[...action]]`, getRouteWithSlug(route.ARTISTS, v));
                }}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

ArtistsSearchFilters.propTypes = {
  t: PropTypes.func.isRequired,
  submitFunc: PropTypes.func,
  professions: PropTypes.array,
  voiceTypes: PropTypes.array,
  artists: PropTypes.array,
  variant: PropTypes.oneOf(['search', 'filter']),
  artistsLoading: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

ArtistsSearchFilters.defaultProps = {
  variant: 'filter',
};

const mapStateToProps = createStructuredSelector({
  professions: selectArtistProfessionsOptions(),
  voiceTypes: selectVoiceTypesOptions(),
  artists: selectProfilesListOptions(),
  artistsLoading: selectProfilesListLoading(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(withTranslation(), withConnect)(ArtistsSearchFilters);
